import { useTranslation } from "react-i18next";
import { CommissionContract } from "../../../api/ComissionContractsAPI";
import SectionTitle from "../../SectionTitle/SectionTitle";
import InputShortText from "../../InputShortText/InputShortText";
import PriceField from "../../PriceField/PriceField";
import NoData from "../../NoData/NoData";
import FileUploadWrapper from "../../FileUploadWrapper/FileUploadWrapper";
import FileList from "../../FileList/FileList";
import CONFIG from "../../../config/config";

interface FilesTabSectionProps {
  form: CommissionContract;
  commission: CommissionContract;
  isEditing: boolean;
  isUploading: boolean;
  onFileChange: (files: File[]) => void;
  onRemoveAttachment: (url: string) => void;
  onAddAddon: () => void;
  onRemoveAddon: (index: number) => void;
  onUpdateAddon: (
    index: number,
    field: "name" | "description" | "cost" | "currency",
    value: string | number
  ) => void;
}

export default function FilesTabSection({
  form,
  commission,
  isEditing,
  isUploading,
  onFileChange,
  onRemoveAttachment,
  onAddAddon,
  onRemoveAddon,
  onUpdateAddon,
}: FilesTabSectionProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div>
        <SectionTitle text={t("store_commission_panel.attachments_title") || "Attachments"} />
        {isEditing && (
          <div className="mb-4">
            <FileUploadWrapper
              label={t("store_commission_panel.add_attachments") || "Add attachments"}
              onChange={onFileChange}
              allowedMultipleFiles={true}
              maxSize={CONFIG.MAX_FILE_UPLOAD}
              allowedTypes={CONFIG.FILE_TYPES}
            />
            {isUploading && (
              <div className="mt-2 text-sm text-blue-600 flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                {t("store_commission_panel.uploading_files") || "Uploading files..."}
              </div>
            )}
          </div>
        )}
        <FileList
          existingFiles={form.attachments || []}
          onRemoveExisting={isEditing ? onRemoveAttachment : undefined}
          isDisabled={!isEditing}
          resourceLinkType="private"
          resourceLinkProps={{
            type: "commission-contracts",
            prefixId: commission.trackingId,
          }}
        />
      </div>

      <div>
        <SectionTitle text={t("store_commission_panel.addons_title") || "Add-ons"} />
        {isEditing && (
          <div className="mb-4">
            <button
              type="button"
              onClick={onAddAddon}
              className="text-blue-600 hover:text-blue-800 text-sm underline mb-4"
            >
              {t("store_commission_panel.add_addon") || "+ Add add-on"}
            </button>
          </div>
        )}
        <div className="space-y-2">
          {form.addons && form.addons.length > 0 ? (
            form.addons.map((addon, index) => (
              <div key={index} className="border rounded p-3 bg-gray-50">
                {isEditing ? (
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <InputShortText
                        label={t("store_commission_panel.addon_name") || "Add-on name"}
                        defaultValue={addon.name}
                        onChange={(e) => onUpdateAddon(index, "name", e.target.value)}
                      />
                    </div>
                    <div className="flex-1">
                      <InputShortText
                        label={t("store_commission_panel.addon_description") || "Description"}
                        defaultValue={addon.description}
                        onChange={(e) => onUpdateAddon(index, "description", e.target.value)}
                      />
                    </div>
                    <div className="flex-1">
                      <PriceField
                        label={t("store_commission_panel.addon_cost") || "Cost"}
                        currency={addon.currency}
                        price={addon.cost}
                        currencies={CONFIG.SUPPORTED_CURRENCIES}
                        onCurrencyChange={(currency) => onUpdateAddon(index, "currency", currency)}
                        onPriceChange={(price) => onUpdateAddon(index, "cost", price)}
                        isDisabled={false}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => onRemoveAddon(index)}
                      className="text-red-600 hover:text-red-800 p-2 rounded hover:bg-red-50 transition-colors mt-6"
                      title={t("store_commission_panel.remove_addon") || "Remove add-on"}
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    <div className="flex-1 text-sm font-medium">{addon.name}</div>
                    <div className="flex-1 text-sm">
                      {addon.description || t("store_commission_panel.no_description") || "No description"}
                    </div>
                    <div className="flex-1 text-sm font-medium">
                      {addon.cost} {addon.currency}
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <NoData message={t("store_commission_panel.no_addons") || "No add-ons"} />
          )}
        </div>
      </div>
    </div>
  );
}
